/* eslint-disable camelcase */
import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import AppBanner from '@modules/layout/layout/components/AppBanner';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppLayout, { LayoutType, TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import useNavigationSearch from '@modules/layout/topNavigation/hooks/useNavigationSearch';
import { Button, makeStyles } from '@rbx/ui';
import { Article_article_banner } from '@modules/client';
import aliasComponents from '@modules/ui/markdown/constants/aliasComponents';
import customComponents from '@modules/ui/markdown/constants/customComponents';
import AssistantNavButton from '@modules/assistant/components/AssistantNavButton';
import { TestId } from '@modules/utilities/constants/testConstants';
import { useIsAuthenticated } from '@modules/assistant/context/IsAuthenticatedProvider';
import { getMDXComponent } from '@rbx/docsite-utilities/dist/browser';
import { getSearchKeyboardShortcut } from '@modules/utilities/utils/getKeyboardShortcut';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import AppIAHeading from './layout/components/AppIAHeading';

export interface LandingProps {
  title: string;
  content: string;
  banner?: Article_article_banner;
  openGraphMeta: Partial<TOpenGraphMeta>;
}

export const maxWidth = 1920;

const useStyles = makeStyles()((theme) => ({
  buttonsContainer: {
    marginTop: 24,
  },

  search: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2),
    verticalAlign: 'top',
  },
}));

// need to be a separate component to make sure
// the hook 'useNavigationSearch' is wrapped by NavigationSearchProvider
const Search: FunctionComponent<React.PropsWithChildren> = () => {
  const {
    classes: { search },
  } = useStyles();
  const { translate } = useTranslation();
  const { setSearchDialogOpenWithEvent } = useNavigationSearch();
  return (
    <Button
      className={search}
      onClick={() =>
        setSearchDialogOpenWithEvent({
          searchDialogOpen: true,
          interaction: ESearchInteraction.LandingSearchButton,
        })
      }
      size='large'
      variant='contained'
      color='primaryBrand'
      aria-label={translate('Label.SearchDocs')}>
      {getSearchKeyboardShortcut(translate('Label.SearchDocs'))}
    </Button>
  );
};

const Landing: FunctionComponent<React.PropsWithChildren<LandingProps>> = ({
  banner,
  content,
  openGraphMeta,
}) => {
  const { isAuthenticated: allowAssistantNav } = useIsAuthenticated();
  const {
    classes: { buttonsContainer },
  } = useStyles();

  const { translate } = useTranslation();
  const contentComponent = useMemo(() => {
    const Component = getMDXComponent(content);
    return <Component components={{ ...aliasComponents(), ...customComponents }} />;
  }, [content]);

  return (
    <AppLayout
      type={LayoutType.Landing}
      testid={TestId.LandingPageLayout}
      openGraphMeta={openGraphMeta}
      pageTitle={translate('Title.Overview')}
      renderBanner={(className?: string) =>
        banner && (
          <AppBanner {...banner} className={className} variant='primary'>
            <div className={buttonsContainer}>
              <Search />
              {/*
              In this context, we're not using the `useIsAuthenticated` hook, as the Assistant component does. 
              When an unauthenticated user clicks the button and is directed to the Assistant page, 
              that page should manage the login process.
            */}
              {allowAssistantNav && <AssistantNavButton />}
            </div>
          </AppBanner>
        )
      }>
      <AppIAHeading title={translate('Heading.Learn')} />
      <AppContent>{contentComponent}</AppContent>
    </AppLayout>
  );
  /* eslint-disable-next react/forbid-component-props */
};

export default Landing;
